import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import GlassCard from '../Ui/GlassCard';
import { motion, AnimatePresence } from 'framer-motion';
import { makeTrace } from '@/utils/operationalTrace';
import rtClient, { subscribeOperationChange, verifyOperationConsistency } from '@/api/realtimeClient';
import { useAuth } from '@/api/AuthContext';
import { playProceduralSound } from '../System/SoundManager';
import { newIdempotencyKey } from '@/utils/idempotencyKey';
import RoomDeactivationDialog from '@/components/Rooms/RoomDeactivationDialog';
import { isShowcaseRoom, isShowcaseVisitor } from '@/utils/consoleAccess';

function utcNow() {
    return new Date().toUTCString();
}

function getSessionHeaders() {
    const sessionId = localStorage.getItem('reacta_sessionId')
        || sessionStorage.getItem('reacta_sessionId')
        || localStorage.getItem('reacta_session_id')
        || sessionStorage.getItem('reacta_session_id');
    const headers = { 'Content-Type': 'application/json' };
    if (sessionId) {
        headers['x-session-id'] = sessionId;
    }
    return headers;
}

const realOperationInitialData = {
    occurrenceName: '',
    company: '',
    city: '',
    state: '',
    address: '',
    coordinates: '',
    occurrenceType: '',
    initialDescription: '',
    involvedProduct: '',
    incidentCenter: '',
};

export default function RoomSelector({ isOpen, onClose, onRoomSelected }) {
    const [visible, setVisible] = useState(isOpen);
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [time, setTime] = useState(utcNow());
    const [newRoomName, setNewRoomName] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [error, setError] = useState(null);
    // SEC-ROOM-PRESERVE-01: sala em ritual de desativacao (nunca exclusao).
    const [deactivationRoom, setDeactivationRoom] = useState(null);
    const createRoomKeyRef = useRef(null);
    const [activeOperationId, setActiveOperationId] = useState(() => rtClient.getActiveOperationId() || null);
    const [operationCreationStep, setOperationCreationStep] = useState('initial'); // 'initial', 'modeSelection', 'realOperation', 'freeOperation', 'quickResponseTemplateSelection'
    const [realOperationData, setRealOperationData] = useState(realOperationInitialData);

    const { user } = useAuth();
    const userRole = user?.role || 'Operator';
    const userName = user?.name || 'guest';
    const userTenant = user?.tenant || 'default';

    // Check if user can create rooms
    const canCreateRooms = ['webmaster', 'institutional_owner', 'super_admin', 'simulation_manager'].includes(userRole.toLowerCase());
    const canDeleteRooms = ['webmaster', 'institutional_owner', 'super_admin'].includes(userRole.toLowerCase());

    const quickResponseTemplates = [
        {
            id: 'carreta-tombada',
            name: 'Carreta Tombada',
            description: 'Inicializa operação com objetivo, fase, áreas (quente, morna, fria), posto de comando e estrutura ICS básica.',
            initialState: {
                currentObjective: 'Remoção segura da carreta e carga, desobstrução da via.',
                operationalPhase: 'CONTAINMENT',
                alertLevel: 'ORANGE',
                activeSector: 'HOT_ZONE',
                commandMode: 'COORDINATED',
            },
        },
        {
            id: 'vazamento-quimico',
            name: 'Vazamento Químico',
            description: 'Inicializa Comando do Incidente, Safety Officer, Operações, Logística, áreas (quente, morna, fria) e objetivo inicial.',
            initialState: {
                currentObjective: 'Contenção do vazamento, isolamento da área e proteção da população.',
                operationalPhase: 'EMERGENCY',
                alertLevel: 'RED',
                activeSector: 'HOT_ZONE',
                commandMode: 'EMERGENCY',
            },
        },
        {
            id: 'incendio-urbano',
            name: 'Incêndio Urbano',
            description: 'Inicializa Comando, Operações, Logística, objetivos iniciais e recursos iniciais.',
            initialState: {
                currentObjective: 'Extinção do incêndio, busca e salvamento de vítimas, proteção de edificações adjacentes.',
                operationalPhase: 'RESCUE',
                alertLevel: 'RED',
                activeSector: 'HOT_ZONE',
                commandMode: 'TACTICAL',
            },
        },
        {
            id: 'acidente-rodoviario',
            name: 'Acidente Rodoviário',
            description: 'Inicializa Comando, Operações, Segurança e objetivos iniciais.',
            initialState: {
                currentObjective: 'Atendimento às vítimas, segurança da cena, desobstrução da via.',
                operationalPhase: 'RESCUE',
                alertLevel: 'YELLOW',
                activeSector: 'WARM_ZONE',
                commandMode: 'COORDINATED',
            },
        },
    ];

    useEffect(() => setVisible(isOpen), [isOpen]);
    useEffect(() => {
        const t = setInterval(() => setTime(utcNow()), 1000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        if (visible) {
            loadRooms();
        }
    }, [visible]);

    const loadRooms = async () => {
        setLoading(true);
        setError(null);
        try {
            const resp = await fetch('/api/rooms/list', { headers: getSessionHeaders() });
            if (resp.ok) {
                const data = await resp.json();
                setRooms(data.rooms || []);
            } else if (resp.status === 401 || resp.status === 403) {
                setRooms([]);
                setError('Autenticação obrigatória para listar salas.');
            } else {
                setRooms([]);
                setError('Nao foi possivel carregar salas do backend autoritativo.');
            }
        } catch (e) {
            console.warn('[RoomSelector] Failed to load rooms from authoritative backend', e);
            setRooms([]);
            setError('Backend de salas indisponivel. Nenhuma sala local sera criada.');
        } finally {
            setLoading(false);
        }
    };

    const handleRealOperationDataChange = (field, value) => {
        setRealOperationData((current) => ({ ...current, [field]: value }));
        if (field === 'occurrenceName' && !newRoomName.trim()) {
            setNewRoomName(value);
        }
    };

    const handleCreateRoom = async (templateId = null, mode = 'free') => {
        let roomNameToCreate = newRoomName.trim();
        const isRealOperation = mode === 'real_operation';

        if (!templateId && !roomNameToCreate) {
            playProceduralSound('error');
            setError('Nome da sala é obrigatório para Operação Livre.');
            return;
        }

        if (templateId) {
            const template = quickResponseTemplates.find(t => t.id === templateId);
            if (!template) {
                playProceduralSound('error');
                setError('Template Quick Response inválido.');
                return;
            }
            roomNameToCreate = template.name; // Use template name as room name for Quick Response
        }

        setError(null);
        if (!createRoomKeyRef.current) createRoomKeyRef.current = newIdempotencyKey();
        try {
            // Get session info for authentication
            const resp = await fetch('/api/rooms/create', {
                method: 'POST',
                headers: getSessionHeaders(),
                body: JSON.stringify({
                    name: roomNameToCreate,
                    idempotencyKey: createRoomKeyRef.current,
                    role: userRole,
                    operationType: isRealOperation ? 'real_operation' : (templateId ? 'quick_response' : 'free_operation'),
                    scenarioType: isRealOperation ? 'operational_template' : (templateId ? 'quick_response_template' : 'free_operation'),
                    templateId: isRealOperation ? 'operacao-real' : templateId,
                    initialOperationData: isRealOperation ? realOperationData : undefined,
                })
            });

            if (resp.ok) {
                const data = await resp.json();
                const newRoom = data.room;
                createRoomKeyRef.current = null;
                playProceduralSound('success');

                // Trace room creation
                try {
                    makeTrace && makeTrace({
                        eventType: 'ROOM_CREATED',
                        operatorId: userName,
                        role: userRole,
                        sourceModule: 'RoomSelector',
                        details: { operationId: newRoom.operationId, name: newRoom.name, templateId: templateId }
                    });
                } catch (e) { }

                // Select the newly created room
                setNewRoomName('');
                setRealOperationData(realOperationInitialData);
                setOperationCreationStep('initial');
                handleSelectRoom(newRoom);
            } else {
                const errorData = await resp.json().catch(() => ({}));
                playProceduralSound('error');
                setError(errorData.details || 'Falha ao criar sala');
            }
        } catch (e) {
            console.error('[RoomSelector] Failed to create room', e);
            playProceduralSound('error');
            setError('Erro ao criar sala. Tente novamente.');
        }
    };

    const handleDeactivateRoom = (e, room) => {
        e.stopPropagation(); // nao seleciona a sala ao abrir o ritual
        setError(null);
        setDeactivationRoom(room);
    };

    const handleDeactivationCompleted = (result) => {
        const operationId = deactivationRoom?.operationId;
        setDeactivationRoom(null);
        playProceduralSound('short_beep');
        if (result?.state === 'deactivated') {
            setRooms(prevRooms => prevRooms.filter(room => room.operationId !== operationId));
        } else {
            loadRooms();
        }
        try {
            makeTrace && makeTrace({
                eventType: result?.state === 'pending_approval' ? 'ROOM_DEACTIVATION_REQUESTED' : 'ROOM_LIFECYCLE_CHANGED',
                operatorId: userName,
                role: userRole,
                sourceModule: 'RoomSelector',
                details: { operationId, state: result?.state || null }
            });
        } catch (e) { }
    };

    const handleSelectRoom = (room) => {
        playProceduralSound('activate');
        try {
            makeTrace && makeTrace({
                eventType: 'ROOM_SELECTED',
                operatorId: userName,
                role: userRole,
                sourceModule: 'RoomSelector',
                details: { operationId: room.operationId, name: room.name }
            });
        } catch (e) { }

        // Store selected room info
        localStorage.setItem('reacta_roomName', room.name);
        localStorage.setItem('reacta_institution', room.institution || room.tenant);
        localStorage.setItem('reacta_roomContext', JSON.stringify({
            operationId: room.operationId,
            name: room.name,
            institution: room.institution || room.tenant,
            tenant: room.tenant || room.institution,
            metadata: room.metadata || {},
            storedAt: new Date().toISOString(),
        }));
        rtClient.setOperationId(room.operationId);

        // Call callback with room info
        if (onRoomSelected) {
            onRoomSelected(room);
        }

        onClose && onClose();
    };

    const handleDirectAccess = () => {
        playProceduralSound('activate');
        // Privileged users can bypass room selection and go directly to admin consoles
        try {
            makeTrace && makeTrace({
                eventType: 'DIRECT_ACCESS_BYPASS',
                operatorId: userName,
                role: userRole,
                sourceModule: 'RoomSelector',
                details: { reason: 'Privileged bypass - direct console access', role: userRole }
            });
        } catch (e) { }

        // Set flags to indicate direct access mode
        localStorage.setItem('reacta_directAccessMode', 'true');
        localStorage.setItem('reacta_bypassRoomSelection', 'true');

        // Close room selector
        onClose && onClose();

        // Navigate directly to appropriate console based on role
        let targetPath = '/AdminConsole';
        if (userRole === 'simulation_manager') {
            targetPath = '/SimulationManager';
        } else if (userRole === 'webmaster' || userRole === 'super_admin') {
            targetPath = '/GodMode';
        } else if (userRole === 'webmaster') {
            targetPath = '/AdminConsole';
        } else if (userRole === 'institutional_owner') {
            targetPath = '/AdminConsole';
        }

        // Trace navigation
        try {
            makeTrace && makeTrace({
                eventType: 'PRIVILEGED_CONSOLE_ACCESS',
                operatorId: userName,
                role: userRole,
                sourceModule: 'RoomSelector',
                details: { targetPath, directAccess: true }
            });
        } catch (e) { }

        // Navigate after small delay
        setTimeout(() => {
            window.history.pushState({}, '', targetPath);
            window.dispatchEvent(new PopStateEvent('popstate'));
        }, 300);
    };

    // 2026-08-06: o backend (/api/rooms/list) ja filtra por tenant real
    // (roomsService.js::userCanSeeRoom, familia ATAC + match exato) antes
    // de responder. Refiltrar aqui com user.tenant zerava a lista pra
    // qualquer conta institucional real (Professor/Operador): esses
    // usuarios nunca tem `user.tenant` (so `user.institution`), entao
    // userTenant caia no fallback 'default', que nao batia com tenant
    // nenhum e escondia todas as salas. Backend ja e autoritativo, nao
    // precisa de segundo filtro client-side.
    const filteredRooms = rooms;

    return createPortal(
        <AnimatePresence>
            {visible && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/75 backdrop-blur-sm"></div>
                    <GlassCard className="relative z-10 w-full max-w-4xl p-6" size="lg">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-2xl font-bold text-cyan-200 tracking-wide">SELEÇÃO DE SALA OPERACIONAL</h2>
                                <div className="text-xs text-amber-300 uppercase tracking-[0.12em]">Room Selection</div>
                                <div className="text-[10px] text-gray-400 mt-1">Escolha ou crie uma sala para iniciar operação</div>
                            </div>
                            <div className="text-xs text-gray-400 font-mono">UTC: {time}</div>
                        </div>

                        <div className="mt-4">
                            {/* User Info */}
                            <div className="p-3 bg-black/30 rounded mb-4">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <div className="text-xs text-gray-400">OPERADOR</div>
                                        <div className="text-sm text-cyan-200 font-mono">{userName}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-400">ROLE</div>
                                        <div className="text-sm text-amber-300">{userRole}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-400">INSTITUIÇÃO</div>
                                        <div className="text-sm text-emerald-300">{userTenant}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Create Room Section (for privileged users) */}
                            {canCreateRooms && (
                                <div className="mb-4 p-4 bg-cyan-900/20 border border-cyan-600/30 rounded">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-sm text-cyan-200 font-bold">CRIAR NOVA SALA</div>
                                        {operationCreationStep === 'initial' && (
                                            <button
                                                onMouseEnter={() => playProceduralSound('button')}
                                                onClick={() => setOperationCreationStep('modeSelection')}
                                                className="px-3 py-1 text-xs bg-cyan-600 text-black rounded hover:bg-cyan-500 transition-colors"
                                            >
                                                + Nova Sala
                                            </button>
                                        )}
                                        {(operationCreationStep === 'modeSelection' || operationCreationStep === 'realOperation' || operationCreationStep === 'freeOperation' || operationCreationStep === 'quickResponseTemplateSelection') && (
                                            <button
                                                onMouseEnter={() => playProceduralSound('button')}
                                                onClick={() => setOperationCreationStep('initial')}
                                                className="px-3 py-1 text-xs bg-red-600 text-black rounded hover:bg-red-500 transition-colors"
                                            >
                                                Cancelar
                                            </button>
                                        )}
                                    </div>

                                    {operationCreationStep === 'modeSelection' && (
                                        <div className="mt-3 space-y-2">
                                            <h3 className="text-sm text-cyan-200 font-bold mb-2">Como deseja iniciar esta operação?</h3>
                                            <button
                                                onMouseEnter={() => playProceduralSound('button')}
                                                onClick={() => setOperationCreationStep('realOperation')}
                                                className="w-full px-4 py-2 bg-amber-600 text-black font-bold rounded hover:bg-amber-500 transition-colors"
                                            >
                                                Opcao 01: Operacao Real
                                            </button>
                                            <button
                                                onMouseEnter={() => playProceduralSound('button')}
                                                onClick={() => setOperationCreationStep('freeOperation')}
                                                className="w-full px-4 py-2 bg-cyan-600 text-black font-bold rounded hover:bg-cyan-500 transition-colors"
                                            >
                                                Opção 01: Operação Livre
                                            </button>
                                            <button
                                                onMouseEnter={() => playProceduralSound('button')}
                                                onClick={() => setOperationCreationStep('quickResponseTemplateSelection')}
                                                className="w-full px-4 py-2 bg-emerald-600 text-black font-bold rounded hover:bg-emerald-500 transition-colors"
                                            >
                                                Quick Response - Ocorrencia Real
                                            </button>
                                            <button
                                                onMouseEnter={() => playProceduralSound('button')}
                                                onClick={() => alert('Funcionalidade em desenvolvimento: Exercício / Simulação')}
                                                className="w-full px-4 py-2 bg-fuchsia-600 text-black font-bold rounded hover:bg-fuchsia-500 transition-colors"
                                            >
                                                Opção 03: Exercício / Simulação
                                            </button>
                                        </div>
                                    )}

                                    {operationCreationStep === 'realOperation' && (
                                        <div className="mt-3 space-y-2">
                                            <input
                                                value={newRoomName}
                                                onChange={(e) => setNewRoomName(e.target.value)}
                                                placeholder="Nome da sala (ex: TPSL Silo 6 - Incendio)"
                                                className="w-full p-2 bg-black/40 border border-amber-500/50 rounded font-mono text-sm text-amber-100"
                                            />
                                            <div className="grid gap-2 md:grid-cols-2">
                                                {[
                                                    ['occurrenceName', 'Nome da ocorrencia'],
                                                    ['company', 'Empresa'],
                                                    ['city', 'Cidade'],
                                                    ['state', 'Estado'],
                                                    ['address', 'Endereco'],
                                                    ['coordinates', 'Coordenadas'],
                                                    ['occurrenceType', 'Tipo da ocorrencia'],
                                                    ['incidentCenter', 'Centro do incidente'],
                                                    ['involvedProduct', 'Produto opcional'],
                                                ].map(([field, placeholder]) => (
                                                    <input
                                                        key={field}
                                                        value={realOperationData[field]}
                                                        onChange={(e) => handleRealOperationDataChange(field, e.target.value)}
                                                        placeholder={placeholder}
                                                        className="w-full p-2 bg-black/40 border border-amber-500/30 rounded font-mono text-xs text-amber-100"
                                                    />
                                                ))}
                                            </div>
                                            <textarea
                                                value={realOperationData.initialDescription}
                                                onChange={(e) => handleRealOperationDataChange('initialDescription', e.target.value)}
                                                placeholder="Descricao inicial confirmada"
                                                className="min-h-[76px] w-full p-2 bg-black/40 border border-amber-500/30 rounded font-mono text-xs text-amber-100"
                                            />
                                            <button
                                                onClick={() => handleCreateRoom(null, 'real_operation')}
                                                className="w-full px-4 py-2 bg-amber-600 text-black font-bold rounded hover:bg-amber-500 transition-colors"
                                            >
                                                CRIAR OPERACAO REAL E ENTRAR
                                            </button>
                                        </div>
                                    )}

                                    {operationCreationStep === 'freeOperation' && (
                                        <div className="mt-3 space-y-2">
                                            <input
                                                value={newRoomName}
                                                onChange={(e) => setNewRoomName(e.target.value)}
                                                placeholder="Nome da sala (ex: Treinamento Turma A)"
                                                className="w-full p-2 bg-black/40 border border-cyan-600/50 rounded font-mono text-sm text-cyan-100"
                                            />
                                            <button
                                                onClick={() => handleCreateRoom()}
                                                className="w-full px-4 py-2 bg-cyan-600 text-black font-bold rounded hover:bg-cyan-500 transition-colors"
                                            >
                                                CRIAR E ENTRAR
                                            </button>
                                        </div>
                                    )}

                                    {operationCreationStep === 'quickResponseTemplateSelection' && (
                                        <div className="mt-3 space-y-2">
                                            <h3 className="text-sm text-cyan-200 font-bold mb-2">Escolha um despacho inicial Quick Response:</h3>
                                            <p className="mb-2 text-xs text-emerald-100/80">
                                                Dados essenciais preliminares para iniciar a ocorrencia real agora; confirmar e completar durante o atendimento.
                                            </p>
                                            {quickResponseTemplates.map((template) => (
                                                <button
                                                    key={template.id}
                                                    onClick={() => handleCreateRoom(template.id)}
                                                    className="w-full px-4 py-2 bg-emerald-600 text-black font-bold rounded hover:bg-emerald-500 transition-colors flex flex-col items-start text-left"
                                                >
                                                    <span className="font-bold">{template.name}</span>
                                                    <span className="text-xs opacity-75 mt-1">{template.description}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {error && (
                                <div className="mb-4 p-3 bg-rose-900/20 border border-rose-600/30 rounded text-rose-300 text-sm">
                                    {error}
                                </div>
                            )}

                            {/* Rooms List */}
                            <div className="space-y-2 max-h-[60vh] overflow-y-auto custom-scrollbar">
                                <div className="text-sm text-gray-400 mb-2">
                                    {loading ? 'Carregando salas...' : `${filteredRooms.length} sala(s) disponível(is)`}
                                </div>

                                {!loading && filteredRooms.length === 0 && (
                                    <div className="p-6 bg-black/20 rounded text-center space-y-4">
                                        <div className="text-amber-300 mb-2">⚠ Nenhuma sala disponível</div>
                                        <div className="text-xs text-gray-400 mb-4">
                                            {canCreateRooms
                                                ? 'Crie uma nova sala para começar ou acesse diretamente os consoles administrativos'
                                                : 'Aguarde o gestor criar uma sala ou entre em contato com o administrador'}
                                        </div>
                                        {canCreateRooms && (
                                            <button
                                                onClick={handleDirectAccess}
                                                className="px-6 py-3 bg-gradient-to-r from-amber-600 to-amber-700 text-black font-bold rounded hover:from-amber-500 hover:to-amber-600 transition-all"
                                            >
                                                🚀 ACESSAR PAINEL ADMINISTRATIVO
                                            </button>
                                        )}
                                    </div>
                                )}

                                {!loading && filteredRooms.map((room) => (
                                    <div
                                        key={room.operationId}
                                        className="p-4 bg-black/30 border border-cyan-600/20 rounded hover:border-cyan-600/50 transition-all cursor-pointer"
                                        onClick={() => handleSelectRoom(room)}
                                    >
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3">
                                                    <div className="text-lg font-bold text-cyan-200">{room.name}</div>
                                                    <div className={`px-2 py-1 text-xs rounded ${room.status === 'ACTIVE' ? 'bg-emerald-600/20 text-emerald-300' : 'bg-gray-600/20 text-gray-400'
                                                        }`}>
                                                        {room.status || 'STANDBY'}
                                                    </div>
                                                </div>
                                                <div className="text-sm text-gray-400 mt-1">
                                                    <span className="font-mono text-cyan-300">{room.operationId}</span>
                                                    {isShowcaseRoom(user, room.operationId) && (
                                                        <span className="ml-2 rounded border border-amber-400/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-200">Demonstração</span>
                                                    )}
                                                    {' • '}
                                                    <span>{room.institution || room.tenant}</span>
                                                </div>
                                                <div className="text-xs text-gray-500 mt-1">
                                                    Criada por {room.createdBy} • {new Date(room.createdAt).toLocaleDateString()}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-xs text-gray-400">PARTICIPANTES</div>
                                                <div className="text-2xl font-bold text-cyan-200">{room.participants || 0}</div>
                                            </div>
                                        </div>
                                        <div className="mt-3 pt-3 border-t border-white/5 flex justify-between items-center">
                                            <button
                                                onClick={() => handleSelectRoom(room)}
                                                className="flex-1 px-4 py-2 bg-cyan-600 text-black font-bold rounded hover:bg-cyan-500 transition-colors"
                                            >
                                                ENTRAR NESTA SALA →
                                            </button>
                                            {canDeleteRooms && room.operationId !== 'SCI26-OP-0148' && !isShowcaseVisitor(user, room.operationId) && (
                                                <button
                                                    onClick={(e) => handleDeactivateRoom(e, room)}
                                                    className="ml-2 px-4 py-2 bg-red-600 text-black font-bold rounded hover:bg-red-500 transition-colors"
                                                    title="Desativar sala (a sala e os registros são preservados)"
                                                >
                                                    {room.metadata?.lifecycle?.pendingDeactivation ? 'DESATIVAÇÃO PENDENTE' : 'DESATIVAR'}
                                                </button>
                                            )}
                                            {deactivationRoom?.operationId === room.operationId && (
                                                <RoomDeactivationDialog
                                                    room={deactivationRoom}
                                                    actor={{ id: user?.id, operatorId: user?.operatorId || user?.operator_id }}
                                                    onClose={() => setDeactivationRoom(null)}
                                                    onCompleted={handleDeactivationCompleted}
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="mt-4 pt-4 border-t border-white/5 flex justify-between items-center">
                            <div className="text-xs text-gray-400">
                                {canCreateRooms ? (
                                    <span className="text-cyan-300">✓ Você pode criar e gerenciar salas • Acesso direto aos consoles</span>
                                ) : (
                                    <span>Acesso limitado às salas da sua instituição</span>
                                )}
                            </div>
                            <div className="flex gap-2">
                                {canCreateRooms && filteredRooms.length > 0 && (
                                    <button
                                        onClick={handleDirectAccess}
                                        className="px-4 py-2 bg-amber-600 border border-amber-500 text-black font-bold rounded hover:bg-amber-500 transition-colors"
                                        title="Acessar painel administrativo sem selecionar sala"
                                    >
                                        Acessar Painel
                                    </button>
                                )}
                                <button
                                    onMouseEnter={() => playProceduralSound('button')}
                                    onClick={onClose}
                                    className="px-4 py-2 bg-black border border-gray-600 text-gray-300 rounded hover:bg-gray-800 transition-colors"
                                >
                                    Voltar
                                </button>
                            </div>
                        </div>
                    </GlassCard>
                </motion.div>
            )}
        </AnimatePresence>, document.body
    );
}
